import * as React from 'react';

import { Form } from '@longline/aqua-ui/containers/Form';
import { Input } from '@longline/aqua-ui/inputs/Input';
import { Validation } from '@longline/aqua-ui/containers/Form/Validation';
import { PrimaryButton } from '@longline/aqua-ui/controls/PrimaryButton';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { useConfirm } from '../../contexts/confirm/ConfirmContext';
import { Guide } from '../../ui/Guide';
import { Pane } from '../../ui/Pane';
import { View } from '@longline/aqua-ui/controls/View';
import { Slider } from '@longline/aqua-ui/inputs/Slider';
import { Dropdown } from '@longline/aqua-ui/inputs/Dropdown';
import { Grid } from '../../ui/Grid';
import { ISpecies } from '../../api/models/ISpecies';
import { speciesModelCodeFor } from '../../types/IProject';

const ProjectPane = () => {
  const confirm = useConfirm();
  const globalState = useGlobalState();

  const handleChangeName = (value: string) => {
    const validation = Validation.getValidation(value, { required: {} });
    if(validation !== null) return;
    globalState.setProject({...globalState.project, name: value});
  }
  
  const handleChangeStructureDepth = (value: string) => {
    const validation = Validation.getValidation(value, { required: {}, isFloat: {}, min: { value: 0 }, max: { value: 1000 } });
    if(validation !== null) return;
    globalState.setProject({...globalState.project, structureDepth: parseFloat(value)});
  }

  const handleChangeStocking = (value: string) => {
    const validation = Validation.getValidation(value, { required: {}, isInt: {}, min: { value: 0 }, max: { value: 10000000 } });
    if(validation !== null) return;
    globalState.setProject({...globalState.project, stocking: parseInt(value)});
  }

  const handleChangeClipping = (value: number) => {
    globalState.setProject({...globalState.project, clipping: value / 100 });
  }  


  // Only species AquaFish/AquaShell can actually model may be chosen; the rest
  // of the customer's list has no modelCode and cannot be simulated.
  const simulatable = React.useMemo(
    () => globalState.species.filter(s => s.modelCode !== null),
    [globalState.species]
  );

  // An unset project species means Atlantic salmon, so resolve through the same
  // helper the simulation uses rather than reading project.species directly.
  const selectedSpecies = simulatable.find(
    s => s.modelCode === speciesModelCodeFor(globalState.project)) ?? null;

  const handleChangeSpecies = (s: ISpecies | null) => {
    globalState.setProject({
      ...globalState.project,
      // Snapshot rather than reference: the saved file keeps the name even if
      // the species table later changes.
      species: s && s.modelCode !== null ? { name: s.name, modelCode: s.modelCode } : null
    });
  }

  const handleImport = () => {
    globalState.startLoadProject();
  }

  const handleDelete = (e?: React.MouseEvent) => {
    if(e?.shiftKey) {
      globalState.clearProject();
    } else {
      confirm.start(globalState.clearProject, "Clear project", "Are you sure you wish to delete all layers, structures and other project data?");
    }
  }

  const handleSave = () => {
    globalState.saveProject();
  }

  return (
    <>
      <Pane.Heading>
        <Pane.Title>Project</Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable action={<SecondaryButton title="Delete" negative icon={SVG.Icons.Delete} onClick={handleDelete}/>} >
        Clear project
      </Pane.Subheader>

      <Pane.Body>
        <Grid>
          <Grid.Item weight={6}>
            <Form.Field
              name="name"
              label="Project name"
              value={globalState.project.name}
              hint="Name of this project."
              wrapper='box'
              onChange={handleChangeName}
              rules={{
                required: {}
              }}
              control={<Input/>}
            />
          </Grid.Item>
          <Grid.Item weight={6}>
            <Form.Field
              name="structureDepth"
              label="Structure depth"
              value={globalState.project.structureDepth}
              hint="Default structure depth (m)"
              wrapper='box'
              onChange={handleChangeStructureDepth}
              control={<Slider padded min={1} max={20} ticks={20} color="white"/>}
            />            
          </Grid.Item>
          <Grid.Item weight={6}>
            <Form.Field
              name="stocking"
              label="Default stocking"
              value={globalState.project.stocking}
              hint="Default number of animals in a structure"
              wrapper='box'
              onChange={handleChangeStocking}
              rules={{
                required: {},
                isInt: {},
                min: { value: 0 },
                max: { value: 10000000 }
              }}
              control={<Input unit="animals"/>}
            />               
          </Grid.Item>
          <Grid.Item weight={6}>
            <Form.Field
              name="species"
              label="Species"
              value={selectedSpecies}
              hint="Species grown in this project."
              wrapper='box'
              onChange={handleChangeSpecies}
              control={<Dropdown
                fluid
                data={simulatable}
                placeholder="Select a species"
                label={(s: ISpecies) => s.name}
              >
                <Dropdown.Column>{(s: ISpecies) => s.name}</Dropdown.Column>
              </Dropdown>}
            />
          </Grid.Item>
          <Grid.Item weight={6}>
            <View label="System" hint="Selected system name">
              {globalState.project.systemUUID ? (globalState.project.systemName ?? 'Unnamed system') : 'No system selected'}
            </View>            
          </Grid.Item>       
          <Grid.Item weight={6}>
            <Form.Field
              name="clipping"
              label="Clipping depth"
              value={globalState.project.clipping * 100}
              hint="Clipping depth (cm)"
              wrapper='box'
              onChange={handleChangeClipping}
              control={<Slider padded min={0} max={1000} ticks={101} snapToTicks hideTicks color="white"/>}
            />            
          </Grid.Item>      
        </Grid>
      </Pane.Body>
      
      <Pane.Footer>
        <PrimaryButton onClick={handleImport}>LOAD</PrimaryButton>
        <PrimaryButton onClick={handleSave}>SAVE</PrimaryButton>
      </Pane.Footer>

      {globalState.selectedStructures.length === 0 &&
        <Pane.Guide>
          <Guide x={0} y={0} title="Project management">
            <ul>
              <li>FINS can load .fins files or import .fins files from desktop FINS.</li>
              <li>FINS can download the current project in .fins format.</li>
            </ul>
          </Guide>
        </Pane.Guide>}
    </>
  )
}

export { ProjectPane }